/**
 * #/season — el acumulado de la liga.
 *
 * `GET /api/season/report` hace todo el trabajo (fold de `MatchReport`s en el
 * server). Acá sólo se elige qué partidos entran y se ordena la salida.
 *
 * Nota del API: con `matchIds` explícitos es ESTRICTO (si uno no existe
 * responde 404 en vez de devolver un acumulado incompleto en silencio); sin
 * `matchIds` toma toda la temporada y saltea los logs inutilizables.
 */
import { useMemo, useState, useSyncExternalStore } from 'react';
import type { TFunction } from '../i18n/index';
import { useT } from '../i18n/use-t';
import { Card, Empty, ErrorBox, Loading } from '../components/States';
import { PlayersTable, TeamSkillsTable } from '../components/SkillTable';
import { errorMessage, useAsync } from '../hooks';
import type { ApiClient } from '../lib/api-client';
import {
  DEFAULT_EFFICIENCY_PRESET,
  getEfficiencyConfig,
  getEfficiencyPreset,
  subscribeEfficiency,
} from '../lib/efficiency';
import { effPct, pct } from '../lib/format';
import {
  MIN_ATTACKS_FOR_RANK,
  rankByAttackEfficiency,
  rankByPoints,
  type RankRow,
} from '../lib/rankings';

export function SeasonPage({ client }: { client: ApiClient }): JSX.Element {
  const [selected, setSelected] = useState<string[]>([]);
  const [applied, setApplied] = useState<string[]>([]);
  const [set, setSet] = useState<number | null>(null);
  const t = useT();

  // La convención de eficiencia se elige en Ajustes; acá se respeta (viaja al
  // API como `?efficiency=`) y se muestra qué convención está activa.
  const effPreset = useSyncExternalStore(subscribeEfficiency, getEfficiencyPreset, getEfficiencyPreset);
  const effName = useMemo(() => getEfficiencyConfig().name, [effPreset]);
  const effParam = effPreset === DEFAULT_EFFICIENCY_PRESET ? undefined : effPreset;

  const matches = useAsync(() => client.listMatches(), [client]);
  const sets = useMemo(() => (set === null ? undefined : [set]), [set]);
  const season = useAsync(
    () => client.getSeasonReport(applied.length > 0 ? applied : undefined, sets, effParam),
    [client, applied.join(','), set, effPreset],
  );

  const toggle = (matchId: string): void => {
    setSelected((prev) =>
      prev.includes(matchId) ? prev.filter((id) => id !== matchId) : [...prev, matchId],
    );
  };

  const teams = season.data?.teams ?? [];
  const efficiencyRank = useMemo(() => rankByAttackEfficiency(teams).slice(0, 10), [teams]);
  const pointsRank = useMemo(() => rankByPoints(teams).slice(0, 10), [teams]);

  return (
    <div>
      <div className="page-head">
        <h1>{t('season.title')}</h1>
        <span className="chip muted" title={t('efficiency.chipTitle')}>
          {t('efficiency.chip', { name: effName })}
        </span>
        <label className="row" style={{ gap: 6 }}>
          <span className="hint">{t('common.sets')}</span>
          <select
            value={set === null ? '' : String(set)}
            onChange={(e) => setSet(e.target.value === '' ? null : Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value="">{t('common.all')}</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {t('common.set', { set: n })}
              </option>
            ))}
          </select>
        </label>
      </div>

      <Card
        title={t('season.includedMatches')}
        actions={
          <div className="row" style={{ gap: 6 }}>
            <button type="button" className="small" onClick={() => setSelected([])}>
              {t('common.all')}
            </button>
            <button
              type="button"
              className="small primary"
              onClick={() => setApplied(selected)}
              disabled={selected.join(',') === applied.join(',')}
            >
              {t('season.apply')}
            </button>
          </div>
        }
      >
        {matches.status === 'error' ? (
          <ErrorBox message={errorMessage(matches.error)} onRetry={matches.reload} />
        ) : null}
        {matches.status === 'loading' && matches.first ? <Loading what={t('season.loadingWhat')} /> : null}
        {(matches.data ?? []).length === 0 && matches.status === 'ok' ? (
          <Empty>{t('season.noMatches')}</Empty>
        ) : (
          <>
            <p className="hint">
              {selected.length === 0
                ? t('season.allSeason')
                : t('season.selectedCount', { count: selected.length })}
            </p>
            <div className="grid-2">
              {(matches.data ?? []).map((match) => (
                <label key={match.matchId} className="check">
                  <input
                    type="checkbox"
                    checked={selected.includes(match.matchId)}
                    onChange={() => toggle(match.matchId)}
                  />
                  <span>{match.name || match.matchId}</span>
                </label>
              ))}
            </div>
          </>
        )}
      </Card>

      {season.status === 'error' ? (
        <ErrorBox message={errorMessage(season.error)} onRetry={season.reload} />
      ) : null}
      {season.status === 'loading' && season.first ? <Loading what={t('season.loadingTotals')} /> : null}

      {season.data ? (
        <>
          <Card
            title={t('season.totals', { count: season.data.matches.length })}
            actions={
              <span className="chip muted">
                {t('season.setsChip', {
                  sets:
                    season.data.setsIncluded === 'all'
                      ? t('season.setsAll')
                      : season.data.setsIncluded.join(', '),
                })}
              </span>
            }
          >
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('season.colTeam')}</th>
                    <th className="num">{t('season.colMatches')}</th>
                    <th className="num">{t('season.colSideout')}</th>
                    <th className="num">{t('season.colBreak')}</th>
                    <th className="num" title={t('season.colReceivedTitle')}>
                      {t('season.colReceived')}
                    </th>
                    <th className="num" title={t('season.colServedTitle')}>
                      {t('season.colServed')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {season.data.teams.map((team) => (
                    <tr key={team.name}>
                      <td>{team.name}</td>
                      <td className="num">{team.matches}</td>
                      <td className="num">{pct(team.sideoutPct)}</td>
                      <td className="num">{pct(team.breakPct)}</td>
                      <td className="num">{team.ralliesReceived}</td>
                      <td className="num">{team.ralliesServed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid-2">
            <Card title={t('season.topEfficiency')}>
              <p className="hint">{t('season.minAttacks', { count: MIN_ATTACKS_FOR_RANK })}</p>
              <RankTable rows={efficiencyRank} format={effPct} unit={t('season.unitAttacks')} t={t} />
            </Card>
            <Card title={t('season.topPoints')}>
              <RankTable
                rows={pointsRank}
                format={(v) => String(v)}
                unit={t('season.unitMatches')}
                t={t}
              />
            </Card>
          </div>

          {season.data.teams.map((team) => (
            <Card key={team.name} title={team.name}>
              <h3>{t('season.bySkill')}</h3>
              <TeamSkillsTable skills={team.skills} />
              <h3 style={{ marginTop: 16 }}>{t('season.players')}</h3>
              <PlayersTable players={team.players} showMatches />
            </Card>
          ))}
        </>
      ) : null}
    </div>
  );
}

function RankTable({
  rows,
  format,
  unit,
  t,
}: {
  rows: readonly RankRow[];
  format: (value: number) => string;
  unit: string;
  t: TFunction;
}): JSX.Element {
  if (rows.length === 0) return <p className="empty">{t('season.noRanked')}</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>{t('common.player')}</th>
            <th>{t('common.team')}</th>
            <th className="num">{t('season.colValue')}</th>
            <th className="num">{unit}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.key}>
              <td>{i + 1}</td>
              <td>{row.player}</td>
              <td>{row.team}</td>
              <td className={`num ${row.value < 0 ? 'neg' : ''}`}>{format(row.value)}</td>
              <td className="num">{row.volume}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
